// Google Sign-In access decision (Phase 1, 2026-10-20). Pure — no Firebase, no network — so every branch is
// unit-tested. Mirrors the backend onboarding gate (pilot_access.require_onboarding_user) and fails closed:
//   backend user_access status admin / approved      → allowed (existing users keep their account as is)
//   status missing (no row yet)                      → allowed ONLY when Firestore allowed_users/{email} has
//                                                       approved === true and the Google email is verified
//   pending / blocked / any other status             → denied
//   status or allowlist unavailable                  → denied ("try again")
// A denial only signs the user out. It never deletes, links or changes a Firebase user.

export const GOOGLE_DENIED_MESSAGES = {
  not_invited: 'האפליקציה בשלב פיילוט סגור.\nלפרטים: support@ritmoiq.com',
  not_active: 'הגישה לחשבון הזה אינה פעילה כרגע.\nלפרטים: support@ritmoiq.com',
  check_failed: 'לא הצלחנו לאמת את הגישה כרגע. נסה שוב בעוד רגע',
};

/**
 * @param {object} p
 * @param {string|null} p.accessStatus  GET /access/status → status, or null when the check failed
 * @param {{allowed: boolean, error?: boolean}|null} p.allowlist  checkAllowedUser result (only read when status is 'missing')
 * @param {boolean} p.emailVerified
 * @returns {{allowed: true} | {allowed: false, reason: string, message: string}}
 */
export function decideGoogleAccess({ accessStatus, allowlist, emailVerified }) {
  const deny = (reason) => ({ allowed: false, reason, message: GOOGLE_DENIED_MESSAGES[reason] });
  if (typeof accessStatus !== 'string' || !accessStatus) return deny('check_failed');
  const status = accessStatus.trim().toLowerCase();
  if (status === 'admin' || status === 'approved') return { allowed: true };
  if (status !== 'missing') return deny('not_active');
  if (!allowlist || allowlist.error) return deny('check_failed');
  if (allowlist.allowed === true && emailVerified === true) return { allowed: true };
  return deny('not_invited');
}

// Firebase popup error codes → Hebrew. Cancelling the popup is not an error (silent).
export const GOOGLE_SILENT_CODES = ['auth/popup-closed-by-user', 'auth/cancelled-popup-request', 'auth/user-cancelled'];

// The one neutral Google failure text: never says whether an account exists or how it signs in.
export const GOOGLE_INCOMPLETE_MSG = 'ההתחברות עם Google לא הושלמה. נסה שוב, או התחבר עם אימייל.';

export function googleErrorMessage(code) {
  if (GOOGLE_SILENT_CODES.includes(code)) return '';
  const errors = {
    // Firebase did not link this Google sign-in to an existing account. Phase 1 never links accounts; the text is
    // the neutral one (it does not confirm that an account exists).
    'auth/account-exists-with-different-credential': GOOGLE_INCOMPLETE_MSG,
    // actionable, and about the browser — not about any account
    'auth/popup-blocked': 'הדפדפן חסם את חלון ההתחברות של Google.\nאפשר חלונות קופצים ונסה שוב.',
    'auth/network-request-failed': 'אין חיבור לרשת. נסה שוב',
    'auth/too-many-requests': 'יותר מדי נסיונות. נסה שוב מאוחר יותר',
    'auth/web-storage-unsupported': 'הדפדפן חוסם אחסון נתונים. לא ניתן להתחבר עם Google',
    'auth/user-disabled': 'לא ניתן להתחבר כרגע.\nלפרטים: support@ritmoiq.com',
  };
  return errors[code] || GOOGLE_INCOMPLETE_MSG;
}
